import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PeekBox, PeekDivider, PeekList, PeekSkeleton, PeekStack, PeekTypography, useTheme, } from "@piiqtechnologies/ui-components";
import { SearchInput } from "@components/common/search-input";
import { AnalyticsArea, useTrackEvent } from "@hooks";
import { PATHS } from "@constants/Paths";
import { useSidebarNavContext } from "@stores/SidebarNavContext";
import { useOrgPath } from "@stores/ActiveOrganizationContext";
import { useSubjectReportsContext } from "@stores/SubjectReportsContext";
import { communityPath } from "@utils/orgPath";
import { CommunityAccordionItem } from "./CommunityAccordionItem";
import messages from "../messages.i18n";
const REPORT_SUBPAGE = /^(?:\/o\/[^/]+)?\/(?:overview|results)\/([0-9a-fA-F]{24})/;
const matchCommunities = (communities, searchTerm) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term)
        return communities;
    return communities.filter((community) => { var _a; return (_a = community.name) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(term); });
};
export const CommunityList = () => {
    const intl = useIntl();
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { communityId: paramCommunityId } = useParams();
    const { communities, total, loading, error, activeCommunityId: publishedCommunityId } = useSidebarNavContext();
    const { getSelectedReportId } = useSubjectReportsContext();
    const orgPath = useOrgPath();
    const track = useTrackEvent();
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedCommunityId, setExpandedCommunityId] = useState(paramCommunityId);
    // Drives which community is open, not which is highlighted: /overview/:id and /results/:id
    // carry a report, not a community, so the owner is recovered through latestReportId. An
    // older report won't match — the report list from DISCOVER-965 is what makes this exact.
    const routeCommunityId = useMemo(() => {
        var _a, _b, _c;
        if (paramCommunityId)
            return paramCommunityId;
        const reportId = (_a = location.pathname.match(REPORT_SUBPAGE)) === null || _a === void 0 ? void 0 : _a[1];
        if (!reportId)
            return publishedCommunityId;
        return ((_c = (_b = communities.find((community) => getSelectedReportId(community._id) === reportId || community.latestReportId === reportId)) === null || _b === void 0 ? void 0 : _b._id) !== null && _c !== void 0 ? _c : publishedCommunityId);
    }, [paramCommunityId, publishedCommunityId, location.pathname, communities, getSelectedReportId]);
    const highlightedCommunityId = paramCommunityId !== null && paramCommunityId !== void 0 ? paramCommunityId : (REPORT_SUBPAGE.test(location.pathname) ? undefined : publishedCommunityId);
    const previousCommunityRef = useRef(routeCommunityId);
    useEffect(() => {
        const previous = previousCommunityRef.current;
        previousCommunityRef.current = routeCommunityId;
        if (!previous || !routeCommunityId || previous === routeCommunityId)
            return;
        track(AnalyticsArea.Navigation, "Community Switched", { fromSubjectId: previous, toSubjectId: routeCommunityId });
    }, [routeCommunityId, track]);
    // Landing on any of a community's pages opens it — including a reload straight onto a report.
    // Home is not a community page, so it resets the accordion instead of leaving one open.
    useEffect(() => {
        if (routeCommunityId) {
            setExpandedCommunityId(routeCommunityId);
            return;
        }
        if (location.pathname === orgPath(PATHS.HOME))
            setExpandedCommunityId(undefined);
    }, [routeCommunityId, location.pathname, orgPath]);
    // One at a time: opening a community closes whichever was open. There is no collapse-only
    // affordance by design — expanding and selecting are the same action.
    const handleOpen = useCallback((communityId) => {
        setExpandedCommunityId((current) => {
            if (current !== communityId)
                track(AnalyticsArea.Navigation, "Community Expanded", { subjectId: communityId });
            return communityId;
        });
        navigate(orgPath(communityPath(communityId)));
    }, [navigate, orgPath, track]);
    const filteredCommunities = useMemo(() => matchCommunities(communities, searchTerm), [communities, searchTerm]);
    const handleSearchChange = useCallback((term) => {
        setSearchTerm(term);
        if (!term.trim())
            return;
        track(AnalyticsArea.Navigation, "Community Searched", {
            resultCount: matchCommunities(communities, term).length,
        });
    }, [communities, track]);
    return (
    // Exact spacing rather than a stack gap: the design specifies each of these distances, and
    // the divider lives here so the 20px below it belongs to one component.
    React.createElement(PeekStack, { sx: { flex: 1, minHeight: 0 } },
        React.createElement(PeekDivider, { orientation: "horizontal", light: true }),
        loading ? (React.createElement(PeekStack, { gap: 1, sx: { px: 1, mt: "20px" } }, Array.from({ length: 6 }).map((_, index) => (React.createElement(PeekSkeleton, { key: index, variant: "text", sx: { fontSize: "h4.fontSize" } }))))) : error ? (React.createElement(PeekTypography, { variant: "body1", color: theme.palette.grey[300], sx: { px: 1, mt: "20px" } }, intl.formatMessage(messages.communityListError))) : (React.createElement(React.Fragment, null,
            React.createElement(PeekTypography, { variant: "body1", color: theme.palette.grey[300], sx: { px: 1, mt: "20px" } }, intl.formatMessage(messages.communityCount, { count: total })),
            React.createElement(PeekBox, { sx: { px: 0.5, mt: "15px" } },
                React.createElement(SearchInput, { placeholder: intl.formatMessage(messages.communitySearchPlaceholder), value: searchTerm, onChange: handleSearchChange, width: "100%", height: "30px" })),
            React.createElement(PeekBox, { sx: { flex: 1, minHeight: 0, mt: "15px", overflowY: "auto", scrollbarWidth: "thin" } }, filteredCommunities.length === 0 ? (React.createElement(PeekTypography, { variant: "body1", color: theme.palette.grey[300], sx: { px: 1 } }, intl.formatMessage(communities.length ? messages.communityNoMatches : messages.communityListEmpty))) : (React.createElement(PeekList, { disablePadding: true }, filteredCommunities.map((community) => {
                var _a;
                return (React.createElement(CommunityAccordionItem, { key: community._id, communityId: community._id, name: community.name, isExpanded: expandedCommunityId === community._id, isActive: highlightedCommunityId === community._id, reportId: (_a = getSelectedReportId(community._id)) !== null && _a !== void 0 ? _a : community.latestReportId, onOpen: handleOpen }));
            }))))))));
};
export default CommunityList;
